import { useQuery } from '@tanstack/react-query'
import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { ArrowLeft, Plus, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { loanProductsQueryOptions } from '@/api/loans-api'
import type { LoanProductStatus, LoanProductSummary } from '@/api/loans-types'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { MoneyDisplay } from '@/components/money-display'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  INTEREST_METHOD_LABELS,
  INTEREST_PERIOD_LABELS,
  LOAN_PRODUCT_STATUSES,
  REPAYMENT_FREQUENCY_LABELS,
  formatRate,
} from '@/features/loans/constants'
import { LoanLink } from '@/features/loans/loan-link'
import { loanPaths, useLoanNavigate, useRawLoanSearch } from '@/features/loans/loan-paths'
import { useLoanPermissions } from '@/features/loans/permissions'

import {
  DEFAULT_PAGE_SIZE,
  loanProductListSearchSchema,
  parseLoanSearch,
  type LoanProductListSearch,
} from './search'

/**
 * The loan products.
 *
 * Reading them needs `loans.product.view`; changing one needs `loans.product.manage` and a
 * countersignature. The list is searchable because a product picker on an application screen is
 * useless against a long list — the endpoint takes a `search` parameter and this uses it rather
 * than filtering a page in the browser.
 */

const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

const helper = dataTableColumns<LoanProductSummary>()

const columns: DataTableColumn<LoanProductSummary>[] = helper.columns([
  helper.accessor('code', {
    header: 'Code',
    enableSorting: false,
    cell: (context) => <span className="font-mono text-xs">{context.row.original.code}</span>,
    meta: { noWrap: true, widthClass: 'w-32', card: 'lead' },
  }),
  helper.accessor('name', {
    header: 'Product',
    enableSorting: false,
    cell: (context) => (
      <span className="font-medium text-heading">{context.row.original.name}</span>
    ),
    meta: { card: 'title' },
  }),
  helper.accessor('status', {
    header: 'Status',
    enableSorting: false,
    cell: (context) => <StatusBadge status={context.row.original.status} />,
    meta: { widthClass: 'w-28', card: 'lead' },
  }),
  helper.display({
    id: 'rate',
    header: 'Rate',
    cell: (context) => {
      const row = context.row.original
      return (
        <span className="flex flex-col gap-0.5">
          <span className="numeric tabular-nums">{formatRate(row.interestRate)}</span>
          <span className="text-2xs text-muted">
            {INTEREST_PERIOD_LABELS[row.interestRatePeriod]} ·{' '}
            {INTEREST_METHOD_LABELS[row.interestMethod]}
          </span>
        </span>
      )
    },
    meta: { widthClass: 'w-52' },
  }),
  helper.display({
    id: 'principal',
    header: 'Principal',
    cell: (context) => (
      <span className="flex flex-col items-end gap-0.5">
        <MoneyDisplay
          value={context.row.original.minPrincipalMinor}
          currency={context.row.original.currencyCode}
          symbol={false}
        />
        <MoneyDisplay
          value={context.row.original.maxPrincipalMinor}
          currency={context.row.original.currencyCode}
          symbol={false}
          emphasis="muted"
        />
      </span>
    ),
    meta: { align: 'right', widthClass: 'w-36', cardLabel: 'Principal range' },
  }),
  helper.display({
    id: 'instalments',
    header: 'Instalments',
    cell: (context) => (
      <span className="numeric tabular-nums">
        {context.row.original.minInstalments}–{context.row.original.maxInstalments}
      </span>
    ),
    meta: { align: 'right', widthClass: 'w-28', cardLabel: 'Instalments' },
  }),
  helper.accessor('repaymentFrequency', {
    header: 'Frequency',
    enableSorting: false,
    cell: (context) => REPAYMENT_FREQUENCY_LABELS[context.row.original.repaymentFrequency],
    meta: { noWrap: true, widthClass: 'w-32' },
  }),
  helper.accessor('requiresGroup', {
    header: 'Lending',
    enableSorting: false,
    cell: (context) => (context.row.original.requiresGroup ? 'Group only' : 'Individual'),
    meta: { noWrap: true, widthClass: 'w-32' },
  }),
])

const SEARCH_DEBOUNCE_MS = 300
const ALL = 'all'

export function LoanProductListPage() {
  const navigate = useLoanNavigate()
  const permissions = useLoanPermissions()
  const search = parseLoanSearch(loanProductListSearchSchema, useRawLoanSearch())

  const [searchText, setSearchText] = useState(search.search ?? '')
  const page = search.page ?? 1
  const pageSize = search.pageSize ?? DEFAULT_PAGE_SIZE

  const updateSearch = (patch: Partial<LoanProductListSearch>) => {
    navigate(loanPaths.products, {
      search: {
        search: search.search,
        status: search.status,
        page: search.page,
        pageSize: search.pageSize,
        ...patch,
      },
    })
  }

  const lastPushed = useRef(search.search ?? '')

  useEffect(() => {
    const fromUrl = search.search ?? ''
    if (fromUrl !== lastPushed.current) {
      lastPushed.current = fromUrl
      setSearchText(fromUrl)
    }
  }, [search.search])

  useEffect(() => {
    const trimmed = searchText.trim()
    if (trimmed === (search.search ?? '')) {
      return
    }

    const timer = window.setTimeout(() => {
      lastPushed.current = trimmed
      navigate(loanPaths.products, {
        search: {
          search: trimmed === '' ? undefined : trimmed,
          status: search.status,
          page: undefined,
          pageSize: search.pageSize,
        },
        replace: true,
      })
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timer)
    }
  }, [searchText, search.search, search.status, search.pageSize, navigate])

  const query = useQuery(
    loanProductsQueryOptions({
      search: search.search,
      status: search.status,
      page,
      pageSize,
    }),
  )

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const current: PaginationState = { pageIndex: page - 1, pageSize }
    const next = typeof updater === 'function' ? updater(current) : updater
    updateSearch({ page: next.pageIndex + 1, pageSize: next.pageSize })
  }

  const hasActiveFilters = Boolean(search.search ?? search.status)

  const clearFilters = () => {
    lastPushed.current = ''
    setSearchText('')
    navigate(loanPaths.products, { search: { pageSize: search.pageSize } })
  }

  return (
    <>
      <LoanLink
        to={loanPaths.accounts}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover max-lg:min-h-11 max-lg:py-2"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        All loans
      </LoanLink>

      <PageHeader
        title="Loan products"
        description="A product is the contract every loan written against it inherits. Amending one prices the next loan, never one already approved."
        actions={
          <>
            {permissions.canConfigureDisclosure ? (
              <Button asChild variant="secondary">
                <LoanLink to={loanPaths.disclosureConfig}>Disclosure headline</LoanLink>
              </Button>
            ) : null}
            {permissions.canManageProducts ? (
              <Button asChild variant="primary">
                <LoanLink to={loanPaths.newProduct}>
                  <Plus />
                  New product
                </LoanLink>
              </Button>
            ) : null}
          </>
        }
      />

      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-3 rounded-card border border-border-subtle bg-surface p-3">
          <div className="min-w-56 flex-1 space-y-1.5">
            <Label htmlFor="product-search">Search</Label>
            <Input
              id="product-search"
              value={searchText}
              autoComplete="off"
              placeholder="Product code or name"
              onChange={(event) => setSearchText(event.target.value)}
            />
          </div>

          <div className="w-44 space-y-1.5">
            <Label htmlFor="product-status-filter">Status</Label>
            <Select
              value={search.status ?? ALL}
              onValueChange={(value) =>
                updateSearch({
                  status: value === ALL ? undefined : (value as LoanProductStatus),
                  page: undefined,
                })
              }
            >
              <SelectTrigger id="product-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Any status</SelectItem>
                {LOAN_PRODUCT_STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {hasActiveFilters ? (
            <Button variant="ghost" onClick={clearFilters}>
              <X />
              Clear
            </Button>
          ) : null}
        </div>

        <DataTable
          columns={columns}
          data={query.data?.items}
          rowCount={query.data?.totalCount}
          pagination={{ pageIndex: page - 1, pageSize }}
          onPaginationChange={onPaginationChange}
          sorting={UNSORTED}
          onSortingChange={ignoreSortingChange}
          isLoading={query.isPending}
          isFetching={query.isFetching}
          error={query.isError ? query.error : null}
          onRetry={() => void query.refetch()}
          getRowId={(row) => row.id}
          onRowActivate={(row) => navigate(loanPaths.product(row.id))}
          pageSizeOptions={[DEFAULT_PAGE_SIZE, 50, 100]}
          emptyTitle={hasActiveFilters ? 'No products match' : 'No loan products yet'}
          emptyDescription={
            hasActiveFilters
              ? 'Nothing was found with these filters.'
              : 'Nothing can be lent until a product exists: it carries the rate, the fees and the penalty rule every loan written against it will inherit.'
          }
          emptyAction={
            hasActiveFilters ? (
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                Clear the filters
              </Button>
            ) : permissions.canManageProducts ? (
              <Button asChild variant="primary" size="sm">
                <LoanLink to={loanPaths.newProduct}>
                  <Plus />
                  New product
                </LoanLink>
              </Button>
            ) : null
          }
        />
      </div>
    </>
  )
}
